import { router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Switch } from '@/components/ui/Switch'
import { RichTextEditor } from '@/components/inbox/RichTextEditor'
import { RichToolbar } from '@/components/inbox/RichToolbar'
import { BannerTargeting } from '@/components/help_center/BannerTargeting'
import { BannerTonePicker } from '@/components/help_center/BannerTonePicker'
import { BannerPreview } from '@/components/help_center/BannerPreview'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { HelpBannerRow, HelpBannerScope, HelpBannerTone } from '@/pages/HelpCenter/types'

interface BannerFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  banner?: HelpBannerRow | null
  categories: { id: string; name: string }[]
  availableTags: string[]
  brandPrimary: string | null
}

// datetime-local wants "YYYY-MM-DDTHH:mm" in local time; the server sends ISO
// with a zone. Round-tripping through Date keeps the wall-clock time the
// author sees consistent with what they picked.
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const offset = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - offset).toISOString().slice(0, 16)
}

// And back. A datetime-local value carries no zone, and the app sets no
// config.time_zone, so Rails would read "2026-09-22T09:00" as 09:00 UTC: an
// author outside UTC would get a banner going live at the wrong wall-clock
// time. new Date() reads it in the browser's zone, which is the one the author
// was thinking in, and toISOString stamps the offset on.
function fromLocalInput(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

interface BannerForm {
  title: string
  bodyHtml: string
  scope: HelpBannerScope
  tags: string[]
  categoryId: string | null
  tone: HelpBannerTone
  accentColor: string | null
  active: boolean
  dismissible: boolean
  startsAt: string
  endsAt: string
}

function initialForm(banner?: HelpBannerRow | null): BannerForm {
  return {
    title: banner?.title ?? '',
    bodyHtml: banner?.body_html ?? '',
    scope: banner?.scope ?? 'all',
    tags: banner?.tags ?? [],
    categoryId: banner?.help_category_id ?? null,
    tone: banner?.tone ?? 'brand',
    accentColor: banner?.accent_color ?? null,
    active: banner?.active ?? true,
    dismissible: banner?.dismissible ?? true,
    startsAt: toLocalInput(banner?.starts_at ?? null),
    endsAt: toLocalInput(banner?.ends_at ?? null),
  }
}

export function BannerFormDialog({ open, onOpenChange, banner, categories, availableTags, brandPrimary }: BannerFormDialogProps) {
  const { withAccount } = useRouteParams()
  const editing = Boolean(banner)

  const [form, setForm] = useState<BannerForm>(() => initialForm(banner))
  const [editor, setEditor] = useState<Editor | null>(null)
  const { title, bodyHtml, scope, tags, categoryId, tone, accentColor, active, dismissible, startsAt, endsAt } = form

  const update = (patch: Partial<BannerForm>) => setForm(prev => ({ ...prev, ...patch }))

  // The dialog stays mounted between sessions, so without this a cancelled
  // draft would still be sitting there on the next open, and reopening a
  // just-saved banner would show the pre-save values rather than the
  // refreshed props.
  useEffect(() => {
    if (open) setForm(initialForm(banner))
  }, [ open, banner ])

  const submit = () => {
    const payload = {
      help_banner: {
        title,
        body_html: bodyHtml,
        scope,
        tags: scope === 'tagged' ? tags : [],
        help_category_id: scope === 'category' ? categoryId : null,
        tone,
        accent_color: accentColor,
        active,
        dismissible,
        starts_at: fromLocalInput(startsAt),
        ends_at: fromLocalInput(endsAt),
      },
    }
    const opts = { preserveScroll: true, onSuccess: () => onOpenChange(false) }

    if (editing && banner) {
      router.patch(routes.helpBanners.update.path(withAccount({ id: banner.id })), payload, opts)
    } else {
      router.post(routes.helpBanners.create.path(withAccount()), payload, opts)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit banner' : 'New banner'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="banner-title">Heading</Label>
            <Input
              id="banner-title"
              value={title}
              onChange={e => update({ title: e.target.value })}
              placeholder="Optional, e.g. You're reading the v1 docs"
            />
          </div>

          <div className="space-y-2">
            <Label>Message</Label>
            <div className="overflow-hidden rounded-lg border border-border">
              <RichToolbar editor={editor} inlineOnly />
              <RichTextEditor
                value={bodyHtml}
                onChange={value => update({ bodyHtml: value })}
                onEditorReady={setEditor}
                inlineOnly
                className="min-h-[90px]"
                placeholder="Keep it to a sentence or two…"
              />
            </div>
          </div>

          <BannerTargeting
            scope={scope}
            tags={tags}
            categoryId={categoryId}
            categories={categories}
            availableTags={availableTags}
            onScopeChange={value => update({ scope: value })}
            onTagsChange={value => update({ tags: value })}
            onCategoryChange={value => update({ categoryId: value })}
          />

          <BannerTonePicker
            tone={tone}
            accentColor={accentColor}
            brandPrimary={brandPrimary}
            onToneChange={value => update({ tone: value })}
            onAccentChange={value => update({ accentColor: value })}
          />

          <div className="space-y-2">
            <Label>Preview</Label>
            <BannerPreview title={title} bodyHtml={bodyHtml} tone={tone} accentColor={accentColor} brandPrimary={brandPrimary} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="banner-starts">Starts</Label>
              <Input id="banner-starts" type="datetime-local" value={startsAt} onChange={e => update({ startsAt: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="banner-ends">Ends</Label>
              <Input id="banner-ends" type="datetime-local" value={endsAt} onChange={e => update({ endsAt: e.target.value })} />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">Leave both empty to show the banner until you switch it off.</p>

          <div className="flex items-center justify-between">
            <div>
              <Label>Visitors can dismiss</Label>
              <p className="text-xs text-muted-foreground">Editing the message shows it again to everyone.</p>
            </div>
            <Switch checked={dismissible} onCheckedChange={value => update({ dismissible: value })} />
          </div>

          <div className="flex items-center justify-between">
            <Label>Active</Label>
            <Switch checked={active} onCheckedChange={value => update({ active: value })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit}>{editing ? 'Save banner' : 'Create banner'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
